import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import organisationService from '../services/organisationService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetches (or re-fetches) the current organisation - exposed so the
  // onboarding modal/Settings can refresh it in place after saving, without
  // a full page reload.
  const refreshOrganisation = useCallback(async () => {
    try {
      const data = await organisationService.getMine();
      setOrganisation(data.organisation);
      return data.organisation;
    } catch (err) {
      console.error('Failed to load organisation:', err);
      return null;
    }
  }, []);

  // Load user (and their organisation) on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const data = await authService.getProfile();
          setUser(data.user);
          await refreshOrganisation();
        } catch (err) {
          console.error('Failed to load user:', err);
          // Token is invalid, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          setOrganisation(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token, refreshOrganisation]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await authService.login(email, password);
      
      // Store token and user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setToken(data.token);
      setUser(data.user);
      await refreshOrganisation();
      
      return { success: true, user: data.user };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  }, [refreshOrganisation]);

  const register = useCallback(async (organisation, userData) => {
    setError(null);
    try {
      // The backend deliberately does not return a session token here - the
      // account must be email-verified (via `login`'s check) before it can
      // be used, so we must not auto-authenticate off the raw register
      // response the way `login` does.
      const data = await authService.register(organisation, userData);
      return { success: true, user: data.user, organisation: data.organisation };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setOrganisation(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    organisation,
    setOrganisation,
    refreshOrganisation,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
